<template>
  <v-container fluid class="pa-6 pa-md-8">
    <div class="page-head">
      <h1 class="page-head__title">Export Transactions</h1>
      <p class="page-head__sub">
        Download transaction records as Excel or CSV.
      </p>
    </div>

    <div class="export-card">
      <img
        :src="require('@/assets/calendar-1.svg')"
        class="export-card__illus"
      />

      <span class="pill pill--brand mb-3">Instructions to follow</span>
      <p class="export-card__lead">
        Choose <kbd class="export-card__kbd">a date range</kbd> to start
        downloading transactions.
      </p>

      <v-menu
        ref="menu"
        v-model="menu"
        :close-on-content-click="false"
        :return-value.sync="dates"
        transition="scale-transition"
        offset-y
        min-width="auto"
        :nudge-left="120"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            outlined
            v-bind="attrs"
            v-on="on"
            class="toolbar-btn text-none mb-2"
            :class="{ 'toolbar-btn--active': dates.length > 0 }"
          >
            <v-icon small left>mdi-calendar-range-outline</v-icon>
            {{ dates.length > 0 ? dateRangeText : "Choose date range" }}
            <v-icon
              v-if="dates.length === 2"
              small
              right
              @click.stop="(dates = []), (chosenDateRange = [])"
              >mdi-close</v-icon
            >
          </v-btn>
        </template>
        <v-date-picker
          v-model="dates"
          color="primary"
          :max="new Date().toISOString().substring(0, 10)"
          range
        >
          <v-spacer></v-spacer>
          <v-btn
            small
            text
            color="primary"
            @click="(menu = false), (dates = []), (chosenDateRange = [])"
          >
            Cancel
          </v-btn>
          <v-btn @click="setFilteredDate" text color="primary">OK</v-btn>
        </v-date-picker>
      </v-menu>

      <template v-if="chosenDateRange.length > 1">
        <p class="export-card__pick">Click a format to download</p>
        <div class="export-card__formats">
          <button
            type="button"
            class="format-tile"
            @click="downloadFile('xlsx')"
          >
            <img src="@/assets/excel.png" class="format-tile__img" />
            <span>Excel</span>
          </button>
          <button
            type="button"
            class="format-tile"
            @click="isLoading ? '' : downloadFile('csv')"
          >
            <img src="@/assets/csv.svg" class="format-tile__img" />
            <span>CSV</span>
          </button>
        </div>
      </template>

      <v-alert
        type="error"
        dense
        v-if="isInvalidDate"
        outlined
        class="mt-4"
      >
        Invalid date provided, kindly check again.
      </v-alert>
      <v-progress-circular
        v-if="isLoading"
        indeterminate
        color="primary"
        size="30"
        width="2"
        class="mt-4"
      />
    </div>

    <SnackBar
      :showSnackBar="showSnackBar"
      :message="message"
      :status="status"
    />
  </v-container>
</template>

<script>
import moment from "moment";
import { loadComponent } from "@/utils/helpers";
import { mapGetters } from "vuex";
import SnackBarMixins from "../../../../mixins/SnackBarMIxins";
export default {
  name: "ExportTransactions",
  data() {
    return {
      menu: false,
      dates: [],
      chosenDateRange: [],
      isInvalidDate: false
    };
  },
  components: {
    SnackBar: loadComponent(`component/SnackBar`)
  },
  mixins: [SnackBarMixins],
  computed: {
    ...mapGetters({
      isLoading: "getIsLoading"
    }),
    dateRangeText() {
      return this.dates.join(" ~ ");
    }
  },
  methods: {
    setFilteredDate() {
      const [startDate, endDate] = this.dates;
      if (moment(startDate).isAfter(moment(endDate))) {
        this.isInvalidDate = true;
        this.$refs.menu.save([]);
      } else {
        this.isInvalidDate = false;
        this.$refs.menu.save(this.dates);
        this.chosenDateRange = this.dates;
      }
    },
    downloadFile(ext) {
      const fileName = `trans-${this.chosenDateRange.join("-")}`;
      const [startDate, endDate] = this.dates;
      this.$store.dispatch("transactions/download", {
        fileName,
        ext,
        startDate,
        endDate
      });
    }
  }
};
</script>

<style scoped>
.export-card {
  max-width: 560px;
  margin: 12px auto 0;
  padding: 40px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-card);
}
.export-card__illus {
  width: 96px;
  margin-bottom: 20px;
  opacity: 0.95;
}
.export-card__lead {
  font-size: 15px;
  color: var(--foreground);
  margin: 0 0 18px;
}
.export-card__kbd {
  background: var(--muted);
  color: var(--foreground);
  border-radius: 6px;
  padding: 2px 6px;
  font-size: 13px;
  box-shadow: none;
}
.export-card__pick {
  margin: 22px 0 12px;
  font-size: 14px;
  font-weight: 500;
  color: var(--muted-foreground);
}
.export-card__formats {
  display: flex;
  gap: 16px;
}
.format-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 96px;
  padding: 16px 12px;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--card);
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  font-size: 13px;
  font-weight: 500;
  color: var(--foreground);
}
.format-tile:hover {
  border-color: var(--signal);
  background: var(--signal-soft);
}
.format-tile__img {
  width: 36px;
  height: 36px;
  object-fit: contain;
}
.toolbar-btn {
  border-color: var(--border) !important;
  color: var(--muted-foreground) !important;
  border-radius: var(--radius) !important;
  height: 40px !important;
  font-weight: 500;
}
.toolbar-btn--active {
  border-color: var(--signal) !important;
  color: var(--signal) !important;
}
</style>
